import { useCallback, useEffect, useState } from 'react';
import { BellRing, CircleAlert, CircleCheck, CircleDashed, CircleHelp, CircleMinus, RefreshCw } from 'lucide-react';
import { Temporal } from '@autoday/core';
import { useStore } from '../store/appStore.ts';
import { getNotificationService, useNotifications } from '../notifications/service.ts';
import { WebPushTransport, type ServerNotificationStatus } from '../notifications/webPush.ts';
import { refreshServerStatus, useServerStatus } from '../notifications/serverStatus.ts';
import { assessReminders, type CheckState, type GuardianAction, type GuardianResult } from '../notifications/guardian.ts';
import { Badge, Button, Panel } from '../ui/kit.tsx';
import { errorText, toast } from '../ui/toast.tsx';
import { relativeFromNow } from '../ui/format.ts';
import { getSyncClient, useSync } from '../sync/syncClient.ts';
import { useAuth } from '../auth/authStore.ts';
import { platform } from '../platform.ts';
import { useUi } from '../App.tsx';
import { tr, useLang } from '../i18n/i18n.ts';
import { formatInstant } from '../i18n/format.ts';

const STATE_LABEL: Record<CheckState, [string, string]> = {
  ok: ['Fungerar', 'Working'],
  attention: ['Behöver kontrolleras', 'Needs checking'],
  risk: ['Problem', 'Problem'],
  unknown: ['Okänt', 'Unknown'],
  na: ['Gäller inte', 'Not applicable'],
};
const ACTION_LABEL: Record<GuardianAction, [string, string]> = {
  'request-permission': ['Tillåt notiser', 'Allow notifications'],
  'open-app-settings': ['Öppna inställningar för notiser', 'Open notification settings'],
  'enable-exact-alarms': ['Tillåt exakta larm', 'Allow exact alarms'],
  'subscribe-push': ['Aktivera push på denna enhet', 'Turn on push on this device'],
  'install-pwa': ['Lägg till på hemskärmen', 'Add to home screen'],
  'connect-account': ['Logga in eller skapa konto', 'Sign in or create an account'],
  'run-test': ['Testa om 1 minut', 'Test in 1 minute'],
  'sync-now': ['Synka nu', 'Sync now'],
  'sign-in-again': ['Logga in igen', 'Sign in again'],
  recheck: ['Kontrollera igen', 'Check again'],
};

export function StateIcon({ state, size = 16 }: { state: CheckState; size?: number }) {
  if (state === 'ok') return <CircleCheck size={size} className="shrink-0 text-ok" aria-hidden />;
  if (state === 'risk') return <CircleAlert size={size} className="shrink-0 text-risk" aria-hidden />;
  if (state === 'attention') return <CircleAlert size={size} className="shrink-0 text-warn" aria-hidden />;
  if (state === 'na') return <CircleMinus size={size} className="shrink-0 text-muted" aria-hidden />;
  return <CircleDashed size={size} className="shrink-0 text-muted" aria-hidden />;
}

/** Påminnelsekoll för den här enheten: samma underlag i vyn och i sammanfattningen på Idag. */
export function useReminderGuardian(detail = false): { result: GuardianResult; server: ServerNotificationStatus | null; serverErr: string | null; recheck: () => Promise<void> } {
  const { device } = useStore();
  const n = useNotifications();
  const sync = useSync();
  const auth = useAuth();
  const svc = getNotificationService();
  // Delad, tidsbegränsad cache (serverStatus.ts): inget API-anrop per omplanering eller omladdning.
  // Sammanfattningen (Idag) frågar servern bara när svaret kan ändra bedömningen: Web Push med
  // behörighet och aktiv prenumeration. Annars avgör de lokala kontrollerna redan (risk/åtgärd).
  const serverMatters = detail || (n.status?.channel === 'web-push' && n.status.permission === 'granted' && n.status.backgroundCapable);
  const { status: server, error: serverErr, checkedAt: serverCheckedAt } = useServerStatus(n.report?.at ?? null, detail, serverMatters);
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine));
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);

  const st = auth.state;
  const result = assessReminders({
    now: new Date().toISOString(),
    online,
    profile: st.status === 'guest' ? 'guest' : 'account',
    session: st.status,
    status: n.status,
    report: n.report,
    plan: n.plan,
    sync,
    server,
    serverError: serverErr,
    serverCheckedAt,
    test: device.notificationTest,
  });
  const recheck = useCallback(async () => {
    await svc.replan();
    if (svc.transport instanceof WebPushTransport) await refreshServerStatus({ force: true });
  }, [svc]);
  return { result, server, serverErr, recheck };
}

export function useGuardianAction(recheck: () => Promise<void>) {
  const ui = useUi();
  const auth = useAuth();
  const svc = getNotificationService();
  return async (a: GuardianAction) => {
    try {
      if (a === 'request-permission') await svc.requestPermission();
      else if (a === 'connect-account') ui.openAuth('login');
      else if (a === 'sign-in-again') ui.openAuth('login', auth.state.status !== 'guest' ? auth.state.profile.email : undefined);
      else if (a === 'subscribe-push' && svc.transport instanceof WebPushTransport) {
        await svc.transport.subscribe();
        await recheck();
      } else if (a === 'run-test') {
        await svc.runTest();
        toast({ kind: 'info', text: tr('Testet är schemalagt om en minut. Lämna appen och kom tillbaka och svara om du såg det.', 'The test is scheduled in one minute. Leave the app, then come back and answer whether you saw it.') });
      } else if (a === 'sync-now') await getSyncClient().syncNow();
      else if (a === 'recheck') await recheck();
      else if ((a === 'open-app-settings' || a === 'enable-exact-alarms' || a === 'install-pwa') && svc.transport.openSettings) await svc.transport.openSettings(a);
      else
        toast({
          kind: 'info',
          text:
            a === 'install-pwa'
              ? tr('Öppna delningsmenyn i Safari och välj "Lägg till på hemskärmen".', 'Open the share menu in Safari and choose "Add to Home Screen".')
              : tr('Öppna webbläsarens webbplatsinställningar (låsikonen vid adressen) och tillåt notiser för AutoDay.', 'Open the browser’s site settings (the lock icon by the address) and allow notifications for AutoDay.'),
        });
    } catch (e) {
      toast({ kind: 'error', text: errorText(e) });
    }
  };
}

export function NotificationStatusView() {
  const { state, device } = useStore();
  const n = useNotifications();
  const svc = getNotificationService();
  const { t } = useLang();
  const tz = state.settings.timeZone;
  const { result, server, serverErr, recheck } = useReminderGuardian(true);
  const act = useGuardianAction(recheck);
  const now = Temporal.Now.instant();
  const test = device.notificationTest;
  const st = n.status;
  const testDue = test && test.result === 'scheduled' && test.fireAt <= new Date().toISOString();
  const tone = result.overall === 'ok' ? 'border-l-ok' : result.overall === 'risk' ? 'border-l-risk' : result.overall === 'unknown' ? 'border-l-border-strong' : 'border-l-warn';

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-5 px-4 py-5 lg:px-8">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('Påminnelsekoll', 'Reminder check')}</h1>
          <p className="text-sm text-muted">{t('Kan påminnelser nå dig på den här enheten? Det här är vad AutoDay kan kontrollera härifrån.', 'Can reminders reach you on this device? This is what AutoDay can check from here.')}</p>
        </div>
        <Button icon={<RefreshCw size={16} aria-hidden />} onClick={() => void recheck()} disabled={n.busy}>
          {n.busy ? t('Kontrollerar…', 'Checking…') : t('Kontrollera igen', 'Check again')}
        </Button>
      </header>

      <Panel className={`border-l-4 p-5 ${tone}`} aria-labelledby="ns-verdict">
        <h2 id="ns-verdict" className="flex items-center gap-2 text-base font-semibold">
          <StateIcon state={result.overall === 'ok' ? 'ok' : result.overall === 'risk' ? 'risk' : result.overall === 'unknown' ? 'unknown' : 'attention'} size={18} />
          {result.headline}
        </h2>
        <p className="mt-1 text-sm">{result.summary}</p>
        <p className="mt-2 text-xs text-muted">
          {result.lastCheckedAt ? `${t('Senast kontrollerat', 'Last checked')} ${formatInstant(result.lastCheckedAt, tz)} (${relativeFromNow(result.lastCheckedAt, now)}).` : t('Inte kontrollerat ännu.', 'Not checked yet.')}{' '}
          {result.stale && result.lastCheckedAt && <span className="text-warn">{t('Informationen kan vara inaktuell – kontrollera igen.', 'The information may be out of date – check again.')}</span>}
        </p>
        {result.actions.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {result.actions.slice(0, 3).map((a, i) => (
              <Button key={a} variant={i === 0 ? 'primary' : 'secondary'} onClick={() => void act(a)}>
                {t(...ACTION_LABEL[a])}
              </Button>
            ))}
          </div>
        )}
        <p className="mt-3 text-xs text-muted">{t('Bedömningen bygger på det appen kan kontrollera. Att en push-tjänst tagit emot ett utskick betyder inte att du sett notisen, och AutoDay övervakar inte enheten i bakgrunden.', 'The assessment is based on what the app can check. A push service accepting a send does not mean you saw the notification, and AutoDay does not monitor the device in the background.')}</p>
      </Panel>

      <Panel className="p-2" aria-labelledby="ns-checks">
        <h2 id="ns-checks" className="flex flex-wrap items-center gap-x-2 px-3 pt-3 text-base font-semibold">
          <BellRing size={18} aria-hidden /> {t('Den här enheten', 'This device')}
          <span className="basis-full text-sm font-normal text-muted sm:basis-auto">{st?.label ?? t('Kanal', 'Channel')}</span>
        </h2>
        <ul className="mt-1 divide-y divide-border">
          {result.checks.map((c) => (
            <li key={c.id} className="flex flex-wrap items-start gap-3 px-3 py-3">
              <StateIcon state={c.state} size={18} />
              {/* Smal skärm: åtgärden hamnar under texten i stället för att tränga ihop den. */}
              <div className="min-w-[12rem] flex-1">
                <p className="flex flex-wrap items-center gap-2 text-sm font-semibold">
                  {c.title}
                  <Badge tone={c.state === 'ok' ? 'ok' : c.state === 'risk' ? 'risk' : c.state === 'attention' ? 'warn' : 'neutral'}>{t(...STATE_LABEL[c.state])}</Badge>
                </p>
                <p className="mt-0.5 text-sm">{c.detail}</p>
                {c.observedAt && (
                  <p className="text-xs text-muted">
                    {t('Observerat', 'Observed')} {formatInstant(c.observedAt, tz)}
                  </p>
                )}
              </div>
              {c.action && c.state !== 'ok' && c.state !== 'na' && (
                <Button className="shrink-0" onClick={() => void act(c.action!)}>
                  {t(...ACTION_LABEL[c.action])}
                </Button>
              )}
            </li>
          ))}
        </ul>
        {st && st.limitations.length > 0 && (
          <details className="mx-3 mb-3 mt-1 rounded-lg border border-border px-3 py-2 text-sm">
            <summary className="min-h-9 cursor-pointer py-1 text-muted">{t('Kanalens begränsningar', 'Limitations of the channel')}</summary>
            <ul className="mt-1 list-disc space-y-1 pl-5 text-muted">
              {st.limitations.map((l) => (
                <li key={l}>{l}</li>
              ))}
            </ul>
          </details>
        )}
      </Panel>

      <Panel className="p-5" aria-labelledby="ns-test">
        <h2 id="ns-test" className="text-base font-semibold">{t('Testa om 1 minut', 'Test in 1 minute')}</h2>
        <p className="mt-1 text-sm text-muted">
          {platform() !== 'web'
            ? t('Testet skickas genom samma kanal som vanliga påminnelser. Tryck, lämna appen, lås telefonen och vänta en minut. Svara om just testnotisen kom på låsskärmen i tid, först efter upplåsning eller inte alls.', 'The test uses the same channel as normal reminders. Tap, leave the app, lock the phone and wait a minute. Report whether the test notification arrived on the lock screen on time, only after unlocking, or not at all.')
            : t('Testet skickas genom samma kanal som vanliga påminnelser. Tryck, lämna sedan appen (stäng fliken eller byt program) och vänta en minut. Kom tillbaka och svara om du såg notisen – appen kan inte själv bevisa att du såg den.', 'The test is sent through the same channel as normal reminders. Tap, then leave the app (close the tab or switch programs) and wait a minute. Come back and answer whether you saw the notification – the app cannot prove by itself that you saw it.')}
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button variant="primary" onClick={() => void svc.runTest()} disabled={!st?.backgroundCapable && st?.channel !== 'none'}>
            {t('Testa om 1 minut', 'Test in 1 minute')}
          </Button>
        </div>
        {test && (
          <div className="mt-3 rounded-lg border border-border p-3 text-sm" role="status">
            <p>
              {t('Senaste test:', 'Latest test:')} {formatInstant(test.scheduledAt, tz)} {t('via', 'via')} {test.channel}.{' '}
              {test.result === 'scheduled'
                ? t(`Kanalen accepterade testet (planerat ${formatInstant(test.fireAt, tz)}). Accepterat är inte samma sak som visat.`, `The channel accepted the test (planned ${formatInstant(test.fireAt, tz)}). Accepted is not the same as shown.`)
                : test.result === 'confirmed'
                  ? t('Du bekräftade att den kom i tid på låsskärmen.', 'You confirmed that it arrived on time on the lock screen.')
                  : test.result === 'late'
                    ? t('Den kom först efter upplåsning eller för sent.', 'It appeared only after unlocking or arrived late.')
                  : test.result === 'not-seen'
                    ? t('Du såg den inte.', 'You did not see it.')
                    : (test.detail ?? '')}
            </p>
            {test.result === 'scheduled' && (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {testDue ? <span>{t('Kom just AutoDay testnotis i tid medan skärmen var låst?', 'Did the AutoDay test notification arrive on time while the screen was locked?')}</span> : <span className="text-muted">{t(`Vänta tills ${formatInstant(test.fireAt, tz)}…`, `Wait until ${formatInstant(test.fireAt, tz)}…`)}</span>}
                <Button onClick={() => void svc.confirmTest('confirmed')} disabled={!testDue}>{t('Ja, på låsskärmen', 'Yes, on the lock screen')}</Button>
                <Button onClick={() => void svc.confirmTest('late')} disabled={!testDue}>{t('Först efter upplåsning / sent', 'Only after unlocking / late')}</Button>
                <Button variant="danger" onClick={() => void svc.confirmTest('not-seen')} disabled={!testDue}>
                  {t('Ingen testnotis', 'No test notification')}
                </Button>
              </div>
            )}
            {test.result === 'late' && <p className="mt-2 text-risk">{t('Notisen nådde inte låsskärmen i tid. Kontrollera telefonens låsskärms- och batteriinställningar och testa igen.', 'The notification did not reach the lock screen on time. Check the phone’s lock-screen and battery settings, then test again.')}</p>}
            {test.result === 'not-seen' && <p className="mt-2 text-risk">{t('Påminnelser når dig troligen inte. Kontrollera behörigheter, batterioptimering och "Stör ej", och testa igen.', 'Reminders probably do not reach you. Check permissions, battery optimisation and "Do not disturb", and test again.')}</p>}
          </div>
        )}
      </Panel>

      <Panel className="p-5" aria-labelledby="ns-plan">
        <h2 id="ns-plan" className="text-base font-semibold">{t('Planerat på denna enhet', 'Planned on this device')}</h2>
        {n.report && (
          <p className="mt-1 text-sm">
            {n.report.channel === 'web-push' || n.report.channel === 'none'
              ? n.report.channel === 'web-push'
                ? t(`${n.plan?.totalPointsInHorizon ?? 0} larmpunkter de närmaste 60 dagarna – skickas av servern.`, `${n.plan?.totalPointsInHorizon ?? 0} alarm points in the next 60 days – sent by the server.`)
                : t(`${n.plan?.totalPointsInHorizon ?? 0} larmpunkter de närmaste 60 dagarna – men denna profil kan inte påminna när appen är stängd.`, `${n.plan?.totalPointsInHorizon ?? 0} alarm points in the next 60 days – but this profile cannot remind you when the app is closed.`)
              : t(`${n.report.verifiedPending ?? 0} av ${n.report.requested} notiser bekräftade i systemets lista${n.report.inexact ? ' (inexakta)' : ''}.`, `${n.report.verifiedPending ?? 0} of ${n.report.requested} notifications confirmed in the system list${n.report.inexact ? ' (inexact)' : ''}.`)}
          </p>
        )}
        {n.report?.coverageUntil && (n.report.channel === 'android-local' || n.report.channel === 'ios-local') && (
          <p className="mt-1 text-sm">
            {t('Lokala notiser schemalagda till', 'Local notifications scheduled until')} <strong>{formatInstant(n.report.coverageUntil, tz)}</strong>.
            {n.report.overflowPoints > 0 ? t(` ${n.report.overflowPoints} senare punkter fylls på när appen öppnas – obegränsad påfyllning offline kan inte utlovas.`, ` ${n.report.overflowPoints} later points are added when the app is opened – unlimited top-up offline cannot be promised.`) : ''}
          </p>
        )}
        <ul className="mt-3 divide-y divide-border text-sm">
          {(n.plan?.scheduled ?? []).slice(0, 8).map((p) => (
            <li key={p.id} className="flex flex-wrap items-baseline gap-2 py-2">
              <span className="w-36 shrink-0 tabular-nums text-muted">{formatInstant(p.fireAt, tz)}</span>
              <span className="flex-1">
                {p.title}
                {p.kind === 'summary' && <span className="text-muted"> ({p.pointIds.length})</span>}
              </span>
              {p.deferredByQuietHours && <Badge>{t('Tysta tider', 'Quiet hours')}</Badge>}
              {p.sensitive && state.settings.hideSensitiveOnLockScreen && <Badge>{t('Dold på låsskärm', 'Hidden on lock screen')}</Badge>}
            </li>
          ))}
        </ul>
        {!n.plan?.scheduled.length && <p className="mt-2 text-sm text-muted">{t('Inga kommande påminnelser de närmaste 60 dagarna.', 'No upcoming reminders in the next 60 days.')}</p>}
      </Panel>

      {(server || serverErr) && (
        <Panel className="p-5 text-sm" aria-labelledby="ns-server">
          <h2 id="ns-server" className="text-base font-semibold">{t('Server och worker', 'Server and worker')}</h2>
          {serverErr ? (
            <p className="mt-1 flex items-center gap-2 text-risk">
              <CircleAlert size={16} aria-hidden /> {serverErr}
            </p>
          ) : server ? (
            <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
              <dt className="text-muted">{t('Worker-heartbeat', 'Worker heartbeat')}</dt>
              <dd className="flex flex-wrap items-center gap-1">
                {server.workerStale ? <CircleAlert size={14} className="text-risk" aria-hidden /> : <CircleCheck size={14} className="text-ok" aria-hidden />}
                {server.workerHeartbeatAt ? `${formatInstant(server.workerHeartbeatAt, tz)} (${relativeFromNow(server.workerHeartbeatAt, now)})` : t('Aldrig', 'Never')}
                {server.workerStale && <span className="text-risk">{t(' – gammalt! Påminnelser skickas inte. Kontrollera att workerprocessen körs.', ' – stale! Reminders are not sent. Check that the worker process is running.')}</span>}
              </dd>
              <dt className="text-muted">{t('Push konfigurerat', 'Push configured')}</dt>
              <dd>{server.pushConfigured ? t('Ja', 'Yes') : t('Nej (VAPID-nycklar saknas på servern)', 'No (VAPID keys missing on the server)')}</dd>
              <dt className="text-muted">{t('Senast accepterat utskick', 'Last accepted send')}</dt>
              <dd>
                {server.lastAcceptedAt ? formatInstant(server.lastAcceptedAt, tz) : '–'} <span className="text-xs text-muted">{t('(accepterat av push-tjänsten, inte bevis för visad notis)', '(accepted by the push service, not proof that it was shown)')}</span>
              </dd>
              <dt className="text-muted">{t('Nästa från servern', 'Next from the server')}</dt>
              <dd>{server.nextPlanned.length ? server.nextPlanned.slice(0, 3).map((x) => `${formatInstant(x.fireAt, tz)} ${x.title}`).join(' · ') : '–'}</dd>
              {server.serverHorizonHours !== undefined && (
                <>
                  <dt className="text-muted">{t('Planerat framåt', 'Planned ahead')}</dt>
                  <dd>
                    {t(`Servern lägger utskick i kö ${server.serverHorizonHours} h i förväg`, `The server queues sends ${server.serverHorizonHours} h ahead`)}
                    {server.lastPendingFireAt ? `; ${t('senast köade:', 'last queued:')} ${formatInstant(server.lastPendingFireAt, tz)}` : t('; inget i kön just nu', '; nothing queued right now')}. {t('Senare påminnelser köas när de närmar sig.', 'Later reminders are queued as they approach.')}
                  </dd>
                </>
              )}
            </dl>
          ) : null}
          {server && server.recentErrors.length > 0 && (
            <div className="mt-3">
              <h3 className="font-semibold">{t('Senaste leveransproblem', 'Recent delivery problems')}</h3>
              <ul className="mt-1 space-y-1">
                {server.recentErrors.slice(0, 5).map((e, i) => (
                  <li key={i}>
                    <span className="tabular-nums text-muted">{formatInstant(e.at, tz)}</span> {e.message}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {server?.otherDevices && server.otherDevices.length > 0 && (
            <div className="mt-3">
              <h3 className="font-semibold">{t('Dina andra enheter', 'Your other devices')}</h3>
              <ul className="mt-1 space-y-1">
                {server.otherDevices.map((d, i) => (
                  <li key={i} className="flex flex-wrap items-center gap-2">
                    <span>{d.name || { web: t('Webbläsare', 'Browser'), android: 'Android', ios: 'iPhone/iPad' }[d.platform]}</span>
                    {d.route === 'web-push' ? (
                      <Badge tone="ok">Web Push</Badge>
                    ) : d.route === 'local' ? (
                      <Badge tone="ok">
                        {t('Lokala notiser till', 'Local notifications until')} {formatInstant(d.localCoverageUntil!, tz)}
                      </Badge>
                    ) : d.remotePush ? (
                      <Badge tone="warn" title={t('Servern skickar en neutral fjärrpush via Google/Apple när telefonen inte själv har schemalagt påminnelsen. Mottagen hos push-tjänsten betyder inte visad.', 'The server sends a neutral remote push via Google/Apple when the phone has not scheduled the reminder itself. Received by the push service does not mean shown.')}>
                        {t('Fjärrpush (reserv)', 'Remote push (fallback)')}
                      </Badge>
                    ) : (
                      <Badge tone="risk">{t('Ingen täckning', 'No coverage')}</Badge>
                    )}
                    {d.unroutable > 0 && <span className="text-risk">{t(`${d.unroutable} kommande påminnelse${d.unroutable === 1 ? '' : 'r'} når inte enheten`, `${d.unroutable} upcoming reminder${d.unroutable === 1 ? '' : 's'} cannot reach the device`)}</span>}
                    {d.lastSeenAt && (
                      <span className="text-xs text-muted">
                        {t('senast aktiv', 'last active')} {relativeFromNow(d.lastSeenAt, now)}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <p className="mt-2 text-xs text-muted">{t('Status som visas inne i appen är inte oberoende driftövervakning. Se docs/NOTIFICATIONS.md för ett valfritt externt heartbeat-larm.', 'Status shown inside the app is not independent operational monitoring. See docs/NOTIFICATIONS.md for an optional external heartbeat alarm.')}</p>
        </Panel>
      )}

      {device.observedErrors.length > 0 && (
        <Panel className="p-5 text-sm" aria-labelledby="ns-err">
          <h2 id="ns-err" className="flex items-center gap-2 text-base font-semibold">
            <CircleHelp size={18} aria-hidden /> {t('Observerade fel', 'Observed errors')}
          </h2>
          <ul className="mt-2 space-y-1">
            {[...device.observedErrors]
              .reverse()
              .slice(0, 10)
              .map((e, i) => (
                <li key={i}>
                  <span className="tabular-nums text-muted">{formatInstant(e.at, tz)}</span> [{e.channel}] {e.message}
                </li>
              ))}
          </ul>
        </Panel>
      )}
    </div>
  );
}
